import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SolicitarVisitaService, PagedRequestOptions } from 'src/app/services/solicitar-visita.service';
import { StorageService } from 'src/app/services/storage.service';
import Swal from 'sweetalert2';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import * as XLSX from 'xlsx';
import { Subscription, Subject, merge, of as observableOf } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, catchError } from 'rxjs/operators';
import { PaginatorIntl } from 'src/app/paginator-intl';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SharedModule } from '@app/shared/shared.module';
import { TipoServicioService } from '@app/services/tipo-servicio.service';
import { ClientesService } from 'src/app/services/clientes.service';

@Component({
  selector: 'app-solicitudes-programadas',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatTableModule, MatButtonModule,
    MatIconModule, MatFormFieldModule, MatInputModule, MatPaginatorModule,
    MatSortModule, MatProgressSpinnerModule, MatTooltipModule, RouterModule,
    MatSelectModule, SharedModule
  ],
  templateUrl: './solicitudes-programadas.component.html', 
  styleUrls: ['./solicitudes-programadas.component.scss'],
  providers: [
    { provide: MatPaginatorIntl, useClass: PaginatorIntl }
  ]
})
export class SolicitudesProgramadasComponent implements OnInit, AfterViewInit, OnDestroy {  
  baseDisplayedColumns: string[] = [
    'id', 
    'fechaIngreso',
    'local', 
    'zona',
    'especialidad',
    'tipoServicio', 
    'tipo_solicitud',
    'tiempo_restante',
    'aprobado_por',
    'fechaVisita',
    'estado', 
    'tecnico',
    'observaciones'
  ];
  displayedColumns: string[] = [...this.baseDisplayedColumns];
  dataSource = new MatTableDataSource<any>();
  loading = true;
  totalItems = 0;
  selectedServiceType: string = '';
  tiposServicio: any[] = [];

  selectedCompany: any = null;
  public isGruman: boolean = false;
  public clientes: any[] = [];
  public selectedClientId: number | null = null;
  private companySubscription: Subscription | null = null;
  private searchSubject = new Subject<string>();
  currentUser: any;

  meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  anios: number[] = [];
  selectedMes: string = '';
  selectedAnio: string = '';

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('input') input!: ElementRef;

  constructor(
    private solicitarVisitaService: SolicitarVisitaService,
    private tipoServicioService: TipoServicioService,
    private storage: StorageService,
    private clientesService: ClientesService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.currentUser = this.storage.getCurrentUser();
  }

  ngOnInit() {
    this.generarAnios();
    this.getTiposServicio();

    // Capturar si venimos desde el Dashboard con un mes específico
    this.route.queryParams.subscribe(params => {
      if (params['mesFacturacion']) {
        const parts = params['mesFacturacion'].split(' ');
        if (parts.length === 2) {
          this.selectedMes = parts[0];
          this.selectedAnio = parts[1];
        }
      }
    });

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      if (this.paginator) this.paginator.pageIndex = 0;
      this.loadSolicitudes();
    });

    this.companySubscription = this.storage.companyChange$.subscribe((company: any) => {
      this.selectedCompany = company;
      this.isGruman = this.selectedCompany?.nombre?.trim().toUpperCase() === 'GRUMAN';
      this.selectedClientId = null;
      if (this.isGruman) {
        this.loadClientes();
      }

      this.updateDisplayedColumns(this.selectedCompany);

      if (this.paginator) {
        this.paginator.pageIndex = 0;
        this.loadSolicitudes();
      }
    });

    const user = this.storage.getItem('currentUser');
    if (user && user.selectedCompany) {      
      this.selectedCompany = user.selectedCompany;
      this.isGruman = this.selectedCompany.nombre?.trim().toUpperCase() === 'GRUMAN';
      this.updateDisplayedColumns(this.selectedCompany);
      if (this.isGruman) {
        this.loadClientes();
      }
    } else {      
      this.updateDisplayedColumns(null);
    }
  }

  // Cargar catálogo de clientes para el dropdown
  loadClientes(): void {
    this.clientesService.getClientesWithGruman().subscribe({
      next: (clientes: any[]) => this.clientes = clientes || [],
      error: (err: any) => console.error('Error al cargar clientes:', err)
    });
  }

  // Evento al seleccionar un cliente en la vista GRUMAN
  onClientFilterChange(): void {
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadSolicitudes();
  }

  ngAfterViewInit() {
    merge(this.sort.sortChange, this.paginator.page)
      .pipe(startWith({}))
      .subscribe(() => this.loadSolicitudes());
  }

  ngOnDestroy() {
    if (this.companySubscription) {
      this.companySubscription.unsubscribe();
    }
  }

  generarAnios(): void {
    const currentYear = new Date().getFullYear();
    for (let y = 2023; y <= currentYear + 2; y++) {
      this.anios.push(y);
    }
  }

  onDateFilterChange() {
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadSolicitudes();
  }

  getTiposServicio(): void {
    this.tipoServicioService.findAll().subscribe({
      next: (data: any) => this.tiposServicio = data,
      error: (err: any) => console.error('Error cargando tipos de servicio', err)
    });
  }

  private updateDisplayedColumns(company: any) {
    const newCols = [...this.baseDisplayedColumns];
    let esGruman = false;

    if (company && company.nombre) {
      const nombreLimpio = company.nombre.trim().toUpperCase();
      if (nombreLimpio === 'GRUMAN') {
        esGruman = true;
      }
    }

    if (esGruman) {
      newCols.push('acciones');
      newCols.splice(2, 0, 'cliente');
    }
    this.displayedColumns = newCols;
  }

  loadSolicitudes() {
    this.loading = true;

    let mesFacturacionStr = '';
    if (this.selectedMes) mesFacturacionStr += this.selectedMes;
    if (this.selectedAnio) mesFacturacionStr += (mesFacturacionStr ? ' ' : '') + this.selectedAnio;

    const options: PagedRequestOptions = {
      page: this.paginator.pageIndex + 1,
      limit: this.paginator.pageSize,
      searchTerm: this.input.nativeElement.value,
      serviceType: this.selectedServiceType,
      sortBy: this.sort.active || 'id',
      sortOrder: this.sort.direction || 'desc',
      mesFacturacion: mesFacturacionStr || undefined
    };

    if (this.isGruman) {
      if (this.selectedClientId) {
        options.clientId = this.selectedClientId;
      }
    } else if (this.selectedCompany?.id) {
      options.clientId = this.selectedCompany.id;
    }

    this.solicitarVisitaService.getSolicitudesProgramadasPaginado(options)
      .pipe(catchError(() => observableOf(null)))
      .subscribe({
        next: (response: any) => {
          this.dataSource.data = response ? response.data : [];
          this.totalItems = response ? response.total : 0;
          this.loading = false;
        },
        error: (error: any) => {
          console.error('Error al cargar solicitudes:', error);
          this.loading = false;
        }
    });
  }

  getAprobadorInfo(usuario: any): { nombre: string, clase: string, tag: string } {
    if (!usuario) return { nombre: 'Automático / N/A', clase: 'badge-gray', tag: 'SISTEMA' };
    
    // Si el perfil incluye 'client', es del cliente. Sino, es Gruman.
    const isCliente = usuario.profile?.toLowerCase().includes('client');
    return {
      nombre: `${usuario.name} ${usuario.lastName}`,
      clase: isCliente ? 'badge-blue' : 'badge-orange',
      tag: isCliente ? 'CLIENTE' : 'GRUMAN'
    };
  }

  applyFilter() {
    this.searchSubject.next(this.input.nativeElement.value);
  }

  onServiceTypeChange() {
    this.paginator.pageIndex = 0;
    this.loadSolicitudes();
  }

  resetFilters() {
    this.input.nativeElement.value = '';
    this.selectedServiceType = '';
    this.selectedClientId = null;
    this.paginator.pageIndex = 0;
    this.sort.active = 'id';
    this.sort.direction = 'desc';
    this.selectedMes = '';
    this.selectedAnio = '';
    this.loadSolicitudes();
  }

  verDetalle(solicitud: any) {
    this.router.navigate([`/transacciones/solicitudes-de-visita/modificar/${solicitud.id}`]);
  }

  exportarExcel() {
    Swal.fire({
      title: 'Generando exportación...',
      text: 'Esto puede tardar unos segundos, por favor espera.',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    let mesFacturacionStr = '';
    if (this.selectedMes) mesFacturacionStr += this.selectedMes;
    if (this.selectedAnio) mesFacturacionStr += (mesFacturacionStr ? ' ' : '') + this.selectedAnio;

    // 1. Construir las opciones (Inyectamos el status directamente por seguridad)
    const options: PagedRequestOptions | any = {
      export: true,
      searchTerm: this.input.nativeElement.value,
      serviceType: this.selectedServiceType,
      sortBy: this.sort.active || 'id',
      sortOrder: this.sort.direction || 'desc',
      mesFacturacion: mesFacturacionStr || undefined,
      status: 'programada'
    };

    if (this.isGruman) {
      if (this.selectedClientId) {
        options.clientId = this.selectedClientId;
      }
    } else if (this.selectedCompany?.id) {
      options.clientId = this.selectedCompany.id;
    }

    // 2. Llamada al servicio
    this.solicitarVisitaService.descargarExcelSolicitudes(options).subscribe({
      next: (response: any) => {
        if (!response || !response.data || response.data.length === 0) {
          Swal.fire('Sin datos', 'No hay solicitudes rechazadas para exportar.', 'warning');
          return;
        }

        // 3. Delegar la generación del Excel al servicio maestro
        this.solicitarVisitaService.exportarExcelMaestro(response.data, 'Programadas');
      },
      error: (err: any) => {
        console.error('Error al exportar:', err);
        Swal.close();
        Swal.fire('Error', 'Hubo un problema al generar el Excel.', 'error');
      }
    });
  }

  canDeleteSolicitud(solicitud: any): boolean {
    const currentUser = this.storage.getItem('currentUser');
    return currentUser?.profile === 'superadmin';
  }

  deleteSolicitud(solicitud: any): void {
    if (!this.canDeleteSolicitud(solicitud)) return;

    Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Deseas eliminar la solicitud #${solicitud.id}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result: any) => {
      if (result.isConfirmed) {
         this.solicitarVisitaService.deleteSolicitud(solicitud.id).subscribe({
          next: () => {
            Swal.fire('Eliminado', 'La solicitud ha sido eliminada.', 'success');
            this.loadSolicitudes();
          },
          error: (error: any) => Swal.fire('Error', 'No se pudo eliminar la solicitud.', 'error')
        });
      }
    });
  }
}